import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Typography,
  IconButton,
  Stack,
  Paper
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import { ParameterChangeDto } from '../../services/formService';

interface FormParameterChangesAlertProps {
  changes: ParameterChangeDto[];
  onApplyChange: (fieldCode: string, newValue: string) => void;
  onApplyAll: () => void;
  onDismiss: () => void;
}

const FormParameterChangesAlert: React.FC<FormParameterChangesAlertProps> = ({
  changes,
  onApplyChange,
  onApplyAll,
  onDismiss
}) => {
  const [expanded, setExpanded] = useState(false);

  if (changes.length === 0) return null;

  const formatValue = (value: string | null | undefined): string => {
    if (!value || value === '') return '(empty)';
    // If it's a JSON array, format it nicely
    if (value.startsWith('[') && value.endsWith(']')) {
      try {
        const arr = JSON.parse(value);
        if (Array.isArray(arr)) {
          return arr.join(', ');
        }
      } catch {
        // Not valid JSON, return as-is
      }
    }
    return value;
  };

  return (
    <Alert
      severity="info"
      sx={{ mb: 2 }}
      onClose={onDismiss}
      icon={<SyncIcon />}
      data-testid="linked-fields-alert"
    >
      <Box sx={{ width: '100%' }}>
        {/* Header row with message and buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="body2">
            {changes.length === 1
              ? 'A linked field has been updated in another form.'
              : `${changes.length} linked fields have been updated in other forms.`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              size="small"
              variant="contained"
              onClick={onApplyAll}
              data-testid="linked-fields-apply-all"
            >
              {changes.length === 1 ? 'Apply Update' : `Apply All (${changes.length})`}
            </Button>
            <IconButton size="small" onClick={() => setExpanded(!expanded)} title={expanded ? 'Hide details' : 'Show details'}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Expandable details */}
        <Collapse in={expanded}>
          <Stack spacing={1} sx={{ mt: 2 }}>
            {changes.map(change => (
              <Paper
                key={change.fieldId}
                variant="outlined"
                sx={{ p: 1.5 }}
                data-testid="linked-field-change"
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body1" fontWeight="medium">
                      {change.fieldDescription || change.fieldCode}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" display="block">
                      {change.sourceDescription}
                    </Typography>
                    <Typography variant="body2" display="block" sx={{ mt: 0.5 }}>
                      <Box component="span" sx={{ color: 'text.secondary' }}>Current:</Box>{' '}
                      {formatValue(change.currentValue)}
                    </Typography>
                    <Typography variant="body2" display="block">
                      <Box component="span" sx={{ color: 'primary.main', fontWeight: 'medium' }}>New:</Box>{' '}
                      {formatValue(change.newValue)}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onApplyChange(change.fieldCode, change.newValue)}
                    sx={{ flexShrink: 0 }}
                  >
                    Apply
                  </Button>
                </Box>
              </Paper>
            ))}
          </Stack>
        </Collapse>
      </Box>
    </Alert>
  );
};

export default FormParameterChangesAlert;
